import React, { useState, useRef } from 'react';
import { View, Text, Animated } from 'react-native';
import i18n from 'i18n-js';
import { connect } from 'react-redux';

import { scale } from '@components/frame';
import { isWeb, dateToSeconds } from '@helpers';

import TouchableElement from '@components/common/touchableElement';
import EditableLine from '@components/common/editableLine';
import WheelTimePicker from '@components/common/WheelTimePicker';
import { SmallLoading } from '@components/loading';

import { font } from '@styles/fonts';
import ColorWrapper from '@styles/colors';

import { getTools, create } from '@reducers/tools';
import { getTools as getClampingTools, create as createClampingTools } from '@reducers/clampingTools';
import { modalFieldBg, TEST_IDS } from '@v2/pages/common/constants/base.constants';

const AddTool = ({ C, getTools, getClampingTools, createClampingTools, create, hide, params = {}, afterSubmit }) => {
  const { ncFileId } = params;
  const date = { h: '0', m: '0', s: '0' };
  const [data, setData] = useState({
    machiningDuration: date,
  });
  const [isLoading, setIsLoading] = useState(false);
  const updateData = (key, v) => setData({ ...data, [key]: v });

  const submitDisabled = isLoading || (ncFileId ? !data.toolName || !data.toolNumber : !data.toolName);

  const submit = async () => {
    setIsLoading(true);
    const toolDetails = {
      toolName: data.toolName,
      memo: data.memo,
      description: data.description,
      stickoutMM: Number(parseInt(data.stickoutMM)) || 0,
    };

    if (ncFileId) {
      await createClampingTools({
        toolNumber: Number(parseInt(data.toolNumber)) || 0,
        memo: data.clampingMemo,
        machiningDuration: dateToSeconds(data.machiningDuration),
        ncFileId: ncFileId,
        ...toolDetails,
      });
    } else {
      await create(toolDetails);
    }

    // await getTools();
    if (ncFileId) {
      await getClampingTools({ ncFileId: ncFileId });
    }

    if (afterSubmit) {
      afterSubmit();
    }
    setIsLoading(false);
    hide();
  };

  const [pressStyle, setPressStyle] = useState({
    machiningDuration: true,
  });

  const baseHeight = 0;
  const animation = {
    machiningDuration: useRef(new Animated.Value(baseHeight)).current,
  };

  const openKey = (key) => {
    if (isWeb) {
      return;
    }

    Object.keys(animation).map((k) => {
      if (k !== key) {
        Animated.timing(animation[k], {
          toValue: baseHeight,
          duration: 500,
          useNativeDriver: false,
        }).start();
      }
    });
    const big = scale(200);

    const pressStyle = Object.assign({}, data);
    if (animation[key] && animation[key]._value == big) {
      Animated.timing(animation[key], {
        toValue: baseHeight,
        duration: 500,
        useNativeDriver: false,
      }).start();
      pressStyle[key] = true;
    } else {
      if (animation[key]) {
        Animated.timing(animation[key], {
          toValue: big,
          duration: 500,
          useNativeDriver: false,
        }).start();
        pressStyle[key] = false;
      }
    }
    setPressStyle(pressStyle);
  };

  const mainInfo = [
    {
      key: 'toolName',
      name: 'Tool Name',
      placeholder: 'Tool Name',
    },
    {
      key: 'description',
      name: 'Description',
      placeholder: 'Description',
    },
    {
      key: 'memo',
      name: 'Memo',
      placeholder: 'Memo',
    },
    {
      key: 'stickoutMM',
      name: 'Stickout',
      placeholder: 'Stickout in mm',
      keyboardType: 'number-pad',
    },
  ];

  const ncFileInfo = [
    {
      name: 'Tool Number',
      key: 'toolNumber',
      placeholder: 'Tool Number',
      keyboardType: 'number-pad',
    },
    {
      name: 'Memo',
      key: 'clampingMemo',
      placeholder: 'Memo',
    },
    {
      name: 'Machining Time',
      Component: WheelTimePicker,
      noArrow: true,
      key: 'machiningDuration',
      isTime: true,
    },
  ];

  return (
    <View testID={TEST_IDS.clampingToolDetails.newToolPopup} style={{ paddingBottom: scale(15) }}>
      <View>
        <View
          style={{
            width: '100%',
            zIndex: 1000,
            height: scale(58),
          }}
        >
          <View style={{ position: 'absolute', zIndex: 1000, left: 0 }}>
            <TouchableElement onPress={() => hide()}>
              {({ boxStyle, pressHoverColor }) => (
                <View style={[boxStyle, { padding: scale(15) }]}>
                  <Text style={[{ color: C(pressHoverColor('System/Light/Blue')) }, font.Body]}>{i18n.t('Cancel')}</Text>
                </View>
              )}
            </TouchableElement>
          </View>
          <View style={{ position: 'absolute', zIndex: 1000, right: 0 }}>
            <TouchableElement
              onPress={() => {
                submitDisabled ? null : submit();
              }}
            >
              {({ boxStyle, pressHoverColor }) => (
                <View style={[boxStyle, { padding: scale(15) }]}>
                  {isLoading ? (
                    <SmallLoading />
                  ) : (
                    <Text style={[{ color: C(pressHoverColor(submitDisabled ? 'Label/Light/Quinary' : 'Link/Light/Primary')) }, font.Headline]}>
                      {i18n.t('Done')}
                    </Text>
                  )}
                </View>
              )}
            </TouchableElement>
          </View>
        </View>
        <View style={{ width: '100%', position: 'absolute', zIndex: 100, top: scale(15), alignItems: 'center' }}>
          <Text
            numberOfLines={1}
            ellipsizeMode="middle"
            style={[{ color: C('Label/Light/Primary'), width: '60%', textAlign: 'center' }, font.Headline]}
          >
            <Text style={[{ color: C('Label/Light/Primary'), width: '100%', textAlign: 'center' }, font.Headline]}>{i18n.t('New Tool')}</Text>
          </Text>
        </View>
      </View>
      <View style={{ paddingHorizontal: scale(15) }}>
        {mainInfo.map((item, index) => (
          <EditableLine
            {...{
              item: {
                value: data[item.key],
                onChange: (v) => updateData(item.key, v),
                onClick: () => openKey(item.key),
                animatedHeight: animation[item.key],
                justifyContent: 'flex-end',
                paddingRight: scale(20),
                isOffset: false,
                small: false,
                ...item,
              },
              bg: modalFieldBg,
              pressStyle: pressStyle[item.key],
              index,
              array: mainInfo,
              C,
              bottomBorderBox: false,
              topBorderBox: false,
              topBorder: index !== 0,
              roundTop: index === 0,
              roundBottom: index === mainInfo.length - 1,
            }}
            key={item.key}
          />
        ))}
      </View>
      {ncFileId && (
        <View>
          <View style={{ marginTop: scale(28), paddingHorizontal: scale(15) }}>
            <Text style={[font['Footnote'], { color: C('Label/Light/Tertiary') }]}>{params?.ncFile}</Text>
          </View>
          <View style={{ marginTop: scale(5), paddingHorizontal: scale(15) }}>
            {ncFileInfo.map((item, index) => (
              <EditableLine
                {...{
                  item: {
                    value: data[item.key],
                    onChange: (v) => updateData(item.key, v),
                    onClick: () => openKey(item.key),
                    animatedHeight: animation[item.key],
                    justifyContent: 'flex-end',
                    paddingRight: scale(20),
                    isOffset: false,
                    small: false,
                    ...item,
                  },
                  bg: modalFieldBg,
                  pressStyle: pressStyle[item.key],
                  index,
                  array: ncFileInfo,
                  C,
                  bottomBorderBox: false,
                  topBorderBox: false,
                  topBorder: index !== 0,
                  roundTop: index === 0,
                  roundBottom: index === ncFileInfo.length - 1,
                }}
                key={item.key}
              />
            ))}
          </View>
        </View>
      )}
    </View>
  );
};

export default connect(({ tools: { tools, loaded } }) => ({ tools, loaded }), {
  getTools,
  create,
  getClampingTools,
  createClampingTools,
})(ColorWrapper(AddTool));
